import { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { DesktopLayout } from "../../components/DesktopLayout";
import { MetricCard } from "../../components/MetricCard";
import { EventTimeline } from "../../components/EventTimeline";
import { supabase } from "../../lib/supabase";
import {
  Users,
  Footprints,
  AlertTriangle,
  Repeat,
  CheckCircle2,
  QrCode,
  Clock,
  Smartphone,
  RefreshCw,
  Radio,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const EMPRESA_ID    = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";
const POLL_INTERVAL = 30000;

const card = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useApp();
  const [atualizando, setAtualizando]         = useState(false);
  const [horaAtual, setHoraAtual]             = useState(new Date());
  const [verTudoTimeline, setVerTudoTimeline] = useState(false);
  const [dadosReais, setDadosReais]           = useState({
    ocorrencias: [], passagens: [], rondas: [], alertas: [],
    plantoes: [], rondasOntemCount: null, ocorrenciasOntemTotal: null,
    loading: true, ultimaAtualizacao: null, erros: [],
  });

  useEffect(() => {
    const t = setInterval(() => setHoraAtual(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const carregarDados = useCallback(async (mostrarSpinner = false) => {
    if (mostrarSpinner) setAtualizando(true);

    const hoje  = new Date(); hoje.setHours(0, 0, 0, 0);
    const amanha = new Date(hoje); amanha.setDate(amanha.getDate() + 1);
    const ontem  = new Date(hoje); ontem.setDate(ontem.getDate() - 1);

    const [resOc, resPt, resRonda, resAlertas, resPlantoes, resRondasOntem, resOcOntem] = await Promise.all([
      supabase.from("liberty_ocorrencias").select("*").eq("empresa_id", EMPRESA_ID).order("created_at", { ascending: false }).limit(50),
      supabase.from("liberty_passagem_turno").select("*").eq("empresa_id", EMPRESA_ID).order("created_at", { ascending: false }).limit(20),
      supabase.from("liberty_ronda_pontos").select("*").eq("empresa_id", EMPRESA_ID).gte("created_at", hoje.toISOString()).lt("created_at", amanha.toISOString()).order("created_at", { ascending: false }),
      supabase.from("alertas_panico").select("*").gte("created_at", hoje.toISOString()).lt("created_at", amanha.toISOString()).order("created_at", { ascending: false }),
      supabase.from("liberty_plantoes").select("id, vigilante_id, vigilante_nome, inicio").eq("empresa_id", EMPRESA_ID).is("fim", null).order("inicio", { ascending: false }),
      supabase.from("liberty_ronda_pontos").select("id", { count: "exact", head: true }).eq("empresa_id", EMPRESA_ID).gte("created_at", ontem.toISOString()).lt("created_at", hoje.toISOString()),
      supabase.from("liberty_ocorrencias").select("id", { count: "exact", head: true }).eq("empresa_id", EMPRESA_ID).gte("created_at", ontem.toISOString()).lt("created_at", hoje.toISOString()),
    ]);

    const erros = [];
    if (resOc.error)      erros.push("ocorrências");
    if (resPt.error)      erros.push("passagens");
    if (resRonda.error)   erros.push("rondas");
    if (resAlertas.error) erros.push("alertas");

    setDadosReais({
      ocorrencias:          resOc.data       || [],
      passagens:            resPt.data       || [],
      rondas:               resRonda.data    || [],
      alertas:              resAlertas.data  || [],
      plantoes:             resPlantoes.data || [],
      rondasOntemCount:     resRondasOntem.count ?? null,
      ocorrenciasOntemTotal: resOcOntem.count ?? null,
      loading: false,
      ultimaAtualizacao: new Date(),
      erros,
    });

    if (mostrarSpinner) setAtualizando(false);
  }, []);

  useEffect(() => {
    carregarDados();
    const id = setInterval(() => carregarDados(), POLL_INTERVAL);
    return () => clearInterval(id);
  }, [carregarDados]);

  // ── Valores derivados ─────────────────────────────────────────────────────

  const vigilantesAtivosHoje = useMemo(() => {
    const map = {};
    for (const r of dadosReais.rondas) {
      const id = r.vigilante_id || r.vigilante_nome || "unknown";
      if (!map[id]) map[id] = { id, nome: r.vigilante_nome || "Vigilante", ultimoScan: r.created_at, total: 0 };
      map[id].total += 1;
      if (new Date(r.created_at) > new Date(map[id].ultimoScan)) map[id].ultimoScan = r.created_at;
    }
    return Object.values(map);
  }, [dadosReais.rondas]);

  const ocorrenciasAbertas = dadosReais.ocorrencias.filter(
    (o) => o.status === "pendente" || o.status === "aberta",
  );

  const altaPrioridade = ocorrenciasAbertas.filter(
    (o) => o.prioridade === "alta" || o.prioridade === "crítica" || o.prioridade === "critica",
  );

  const semaforo = !dadosReais.loading
    ? altaPrioridade.length > 0
      ? { cor: "#ef4444", bg: "rgba(239,68,68,0.1)",    border: "rgba(239,68,68,0.28)",    msg: `${altaPrioridade.length} ocorrência${altaPrioridade.length > 1 ? "s" : ""} de alta prioridade em aberto` }
      : ocorrenciasAbertas.length > 0
      ? { cor: "#f59e0b", bg: "rgba(245,158,11,0.08)",  border: "rgba(245,158,11,0.25)",   msg: `${ocorrenciasAbertas.length} ocorrência${ocorrenciasAbertas.length > 1 ? "s" : ""} aberta${ocorrenciasAbertas.length > 1 ? "s" : ""} aguardando resolução` }
      : { cor: "#10b981", bg: "rgba(16,185,129,0.08)",  border: "rgba(16,185,129,0.22)",   msg: `Plantão tranquilo — ${dadosReais.rondas.length} ponto${dadosReais.rondas.length !== 1 ? "s" : ""} confirmado${dadosReais.rondas.length !== 1 ? "s" : ""}, nenhuma ocorrência aberta` }
    : null;

  // Alerta: passagem de turno pendente
  const hora    = horaAtual.getHours();
  const minutos = horaAtual.getMinutes();
  const minutosParaFimTurno = (() => {
    const agora = hora * 60 + minutos;
    if (agora >= 6 * 60 && agora < 7 * 60)   return 7 * 60  - agora;
    if (agora >= 18 * 60 && agora < 19 * 60) return 19 * 60 - agora;
    return null;
  })();
  const passagemPendente =
    !dadosReais.loading &&
    minutosParaFimTurno !== null &&
    !dadosReais.passagens.some((p) => Date.now() - new Date(p.created_at).getTime() < 2 * 3600000);

  const ultimoScanDia       = dadosReais.rondas[0] || null;
  const minDesdeUltimaRonda = ultimoScanDia
    ? Math.round((Date.now() - new Date(ultimoScanDia.validado_em || ultimoScanDia.created_at).getTime()) / 60000)
    : null;

  const timeline = useMemo(() => {
    const eventos = [
      ...dadosReais.rondas.map((r) => ({ id: r.id, criadoEm: r.validado_em || r.created_at, tipo: "ronda_iniciada", tipoLabel: "Ronda QR", titulo: `${r.vigilante_nome || "Vigilante"} · ${r.ponto || "Ponto"}`, subtitulo: r.observacao || "Ponto de ronda confirmado via QR Code." })),
      // Ocultos temporariamente — reativar quando necessário:
      // ...dadosReais.ocorrencias.map((o) => ({ ... })),
      // ...dadosReais.passagens.map((p) => ({ ... })),
      // ...dadosReais.alertas.map((e) => ({ ... })),
    ];
    return eventos.sort((a, b) => new Date(b.criadoEm) - new Date(a.criadoEm));
  }, [dadosReais.rondas]); // eslint-disable-line

  const timelineResumo = useMemo(() => timeline.slice(0, 6), [timeline]);

  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const fmtDateTime = (iso) =>
    new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

  return (
    <DesktopLayout
      title="Dashboard"
      subtitle="Monitoramento de rondas em tempo real."
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => carregarDados(true)}
            disabled={atualizando}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium transition-all disabled:opacity-40"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.6)" }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${atualizando ? "animate-spin" : ""}`} />
            Atualizar
          </button>
          {user?.role === "esquematiza" && (
            <button
              onClick={() => navigate("/vigilante")}
              className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium transition-all"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.6)" }}
            >
              <Smartphone className="w-3.5 h-3.5" />
              Ver como Vigilante
            </button>
          )}
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-xl"
            style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}
          >
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
            </span>
            <span className="text-[12px] font-medium" style={{ color: "#34d399" }}>Ao vivo</span>
          </div>
        </div>
      }
    >
      {dadosReais.erros.length > 0 && (
        <div
          className="rounded-xl px-4 py-3 mb-4 text-[13px]"
          style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", color: "#f87171" }}
        >
          <strong>Erro ao carregar dados:</strong> {dadosReais.erros.join(", ")}. Verifique o console e as políticas RLS.
        </div>
      )}

      {/*
        Ocultos temporariamente — reativar quando necessário:
        - Banner alta prioridade (data-testid="banner-alta-prioridade")
        - Semáforo de status no hero (data-testid="semaforo-status")
        - Indicador vigilante-plantao / sem-vigilante-plantao
        - Alerta passagem pendente (data-testid="alerta-passagem-pendente")
        - KPIs: metric-vigilantes, metric-ocorrencias, metric-passagens
        - Seção "Plantão agora" (aside)
        - Seção "Ocorrências abertas" (aside)
        - Seção "Passagens recentes" (linha inferior)
      */}

      {/* Hero — foco em rondas */}
      <section
        className="rounded-2xl px-6 py-5 mb-4 flex items-center justify-between gap-4"
        style={{
          background: "linear-gradient(135deg, rgba(37,99,235,0.18) 0%, rgba(16,185,129,0.08) 100%)",
          border: "1px solid rgba(59,130,246,0.18)",
        }}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.25)" }}
          >
            <Radio className="w-5 h-5" style={{ color: "#3B82F6" }} />
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-[0.15em] uppercase mb-0.5" style={{ color: "rgba(147,197,253,0.6)" }}>
              CENTRO OPERACIONAL
            </p>
            <h2 className="text-[20px] font-bold text-white leading-tight">Liberty Place</h2>
            <p className="text-[12px] mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
              Condomínio Residencial
            </p>
          </div>
        </div>
        <div className="hidden md:flex flex-col items-end gap-1">
          <p className="text-[28px] font-bold text-white font-mono leading-none">
            {horaAtual.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
          </p>
          <p className="text-[12px]" style={{ color: "rgba(255,255,255,0.35)" }}>
            {horaAtual.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
      </section>

      {/* KPI — somente rondas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <MetricCard
          testid="metric-rondas"
          label="Pontos confirmados hoje"
          value={dadosReais.loading ? "—" : dadosReais.rondas.length}
          delta={dadosReais.rondasOntemCount !== null ? `${dadosReais.rondasOntemCount} ontem` : "via QR Code"}
          icon={Footprints}
          accent="blue"
          onClick={() => navigate("/rondas")}
        />
        <MetricCard
          testid="metric-vigilantes"
          label="Vigilantes com rondas"
          value={dadosReais.loading ? "—" : vigilantesAtivosHoje.length}
          delta="com pontos confirmados hoje"
          icon={Users}
          accent="emerald"
        />
      </div>

      {/* Grid principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Timeline rondas */}
        <section className="lg:col-span-2 rounded-2xl p-6" style={card}>
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-1" style={{ color: "rgba(255,255,255,0.3)" }}>
                Rondas de hoje
              </p>
              <h3 className="text-[17px] font-bold text-white">Pontos confirmados</h3>
            </div>
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold"
              style={{ background: "rgba(16,185,129,0.08)", color: "#34d399", border: "1px solid rgba(16,185,129,0.15)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              ao vivo
            </div>
          </div>

          {dadosReais.loading ? (
            <div className="space-y-3">
              {[1,2,3].map((i) => (
                <div key={i} className="h-14 rounded-xl animate-pulse" style={{ background: "rgba(255,255,255,0.04)" }} />
              ))}
            </div>
          ) : timeline.length > 0 ? (
            <>
              <EventTimeline events={verTudoTimeline ? timeline : timelineResumo} />
              {timeline.length > 6 && (
                <button
                  onClick={() => setVerTudoTimeline((v) => !v)}
                  className="mt-4 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-[12px] font-medium transition-all"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.45)" }}
                >
                  {verTudoTimeline ? (
                    <><ChevronUp className="w-3.5 h-3.5" /> Mostrar menos</>
                  ) : (
                    <><ChevronDown className="w-3.5 h-3.5" /> Ver todos os {timeline.length} pontos</>
                  )}
                </button>
              )}
            </>
          ) : (
            <div className="py-14 text-center">
              <p className="text-[13px]" style={{ color: "rgba(255,255,255,0.25)" }}>Nenhum ponto de ronda confirmado hoje.</p>
            </div>
          )}
        </section>

        {/* Coluna direita — monitoramento */}
        <aside className="space-y-4">
          {/* Rondas QR · hoje */}
          <section className="rounded-2xl p-5" style={card}>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)" }}
              >
                <QrCode className="w-4 h-4" style={{ color: "#3B82F6" }} />
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color: "rgba(255,255,255,0.3)" }}>Ronda QR · hoje</p>
                <h3 className="text-[15px] font-bold text-white leading-tight">
                  {dadosReais.loading ? "—" : dadosReais.rondas.length} scans
                </h3>
              </div>
            </div>
            <div className="space-y-1.5">
              {dadosReais.rondas.slice(0, 6).map((r) => (
                <div
                  key={r.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-[12px]"
                  style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.12)" }}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#10B981" }} />
                  <div className="flex-1 min-w-0">
                    <span className="truncate block" style={{ color: "rgba(255,255,255,0.7)" }}>{r.ponto || "Ponto"}</span>
                    {r.vigilante_nome && (
                      <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.3)" }}>{r.vigilante_nome.split(" ")[0]}</span>
                    )}
                  </div>
                  <span className="flex items-center gap-1 flex-shrink-0 font-mono text-[11px]" style={{ color: "rgba(255,255,255,0.3)" }}>
                    <Clock className="w-3 h-3" />
                    {fmtTime(r.validado_em || r.created_at)}
                  </span>
                </div>
              ))}
              {!dadosReais.loading && dadosReais.rondas.length === 0 && (
                <p className="text-[13px] text-center py-4" style={{ color: "rgba(255,255,255,0.25)" }}>Nenhum ponto confirmado ainda.</p>
              )}
              {dadosReais.loading && (
                <div className="space-y-1.5">
                  {[1,2,3].map((i) => <div key={i} className="h-8 rounded-xl animate-pulse" style={{ background: "rgba(255,255,255,0.04)" }} />)}
                </div>
              )}
            </div>
          </section>

          {/* Monitoramento */}
          <section className="rounded-2xl p-5" style={card}>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)" }}
              >
                <Footprints className="w-4 h-4" style={{ color: "#10B981" }} />
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color: "rgba(255,255,255,0.3)" }}>Rondas · status</p>
                <h3 className="text-[15px] font-bold text-white leading-tight">Monitoramento</h3>
              </div>
            </div>

            {dadosReais.loading ? (
              <div className="space-y-2">
                {[1,2].map((i) => <div key={i} className="h-16 rounded-xl animate-pulse" style={{ background: "rgba(255,255,255,0.04)" }} />)}
              </div>
            ) : ultimoScanDia ? (
              <div className="space-y-2.5">
                <div className="rounded-xl p-3" style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.12)" }}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "rgba(255,255,255,0.3)" }}>Última ronda confirmada</p>
                  <p className="text-[13px] font-semibold text-white/90">{ultimoScanDia.ponto || "Ponto"}</p>
                  <p className="text-[12px] mt-0.5" style={{ color: "rgba(255,255,255,0.45)" }}>
                    {ultimoScanDia.vigilante_nome || "Vigilante"} ·{" "}
                    <span style={{ color: "#34d399" }}>{fmtTime(ultimoScanDia.validado_em || ultimoScanDia.created_at)}</span>
                  </p>
                </div>
                <div
                  data-testid="tempo-sem-ronda"
                  className="rounded-xl p-3"
                  style={{
                    background: minDesdeUltimaRonda > 240 ? "rgba(239,68,68,0.06)" : minDesdeUltimaRonda > 120 ? "rgba(245,158,11,0.06)" : "rgba(16,185,129,0.06)",
                    border: `1px solid ${minDesdeUltimaRonda > 240 ? "rgba(239,68,68,0.18)" : minDesdeUltimaRonda > 120 ? "rgba(245,158,11,0.18)" : "rgba(16,185,129,0.15)"}`,
                  }}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "rgba(255,255,255,0.3)" }}>Última ronda há</p>
                  <p className="text-[14px] font-bold" style={{ color: minDesdeUltimaRonda > 240 ? "#f87171" : minDesdeUltimaRonda > 120 ? "#fbbf24" : "#34d399" }}>
                    {minDesdeUltimaRonda < 60 ? `${minDesdeUltimaRonda} min` : `${Math.floor(minDesdeUltimaRonda / 60)}h ${minDesdeUltimaRonda % 60}min`}
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.35)" }}>
                    {minDesdeUltimaRonda > 240 ? "⚠ Intervalo longo — verificar" : minDesdeUltimaRonda > 120 ? "Monitorar próxima ronda" : "Dentro do intervalo normal"}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-[13px] text-center py-4" style={{ color: "rgba(255,255,255,0.25)" }}>Nenhum ponto confirmado hoje.</p>
            )}
          </section>
        </aside>
      </div>
    </DesktopLayout>
  );
}
